import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Layers, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const num = (n, d = 4) => (n == null ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: d }));
const getPosition = () => api.get("/admin/lp-position", { withCredentials: true }).then((r) => r.data);

const Row = ({ label, value, testId }) => (
  <div className="p-3 rounded-lg bg-[#050505] border border-[#2A2411]">
    <p className="text-[11px] text-zinc-500 uppercase tracking-wider">{label}</p>
    <p data-testid={testId} className="font-display font-bold text-white">{value}</p>
  </div>
);

export const PoolDepositCard = () => {
  const queryClient = useQueryClient();
  const { data, isError } = useQuery({ queryKey: ["adminLp"], queryFn: getPosition, refetchInterval: 15000, retry: false });
  const [percent, setPercent] = useState("100");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const pct = Math.min(Math.max(parseInt(percent, 10) || 0, 0), 100);

  const withdraw = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/lp-withdraw", { percent: pct }, { withCredentials: true });
      toast.success(`Withdrew ${pct}%: ${num(r.fmiToTreasury)} FMI + ${num(r.usdtToTreasury, 2)} USDT sent to the treasury`);
      queryClient.invalidateQueries({ queryKey: ["adminLp"] });
      queryClient.invalidateQueries({ queryKey: ["adminOverview"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Withdrawal failed");
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  return (
    <Card data-testid="admin-lp-card" className="border-[#2A2411] bg-[#0A0A0A]">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-display flex items-center gap-2"><Layers size={16} className="text-[#D4AF37]" /> Treasury deposit in the community pool</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isError && <p data-testid="admin-lp-error" className="text-sm text-red-400">Could not load the pool deposit</p>}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Row label="Pool share" value={data ? `${num(data.sharePercent, 4)}%` : "—"} testId="admin-lp-share" />
          <Row label="FMI" value={num(data?.fmi)} testId="admin-lp-fmi" />
          <Row label="USDT" value={num(data?.usdt, 2)} testId="admin-lp-usdt" />
          <Row label="Value" value={data?.price != null ? `$${num(data.usdt * 2, 2)}` : "—"} testId="admin-lp-value" />
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <span className="text-sm text-zinc-400">Withdraw</span>
          <Input data-testid="admin-lp-percent" type="number" min="1" max="100" value={percent} onChange={(e) => setPercent(e.target.value)}
            className="bg-[#050505] border-[#2A2411] text-white w-24" />
          <span className="text-sm text-zinc-400">% to the treasury</span>
          <Button data-testid="admin-lp-withdraw-btn" disabled={busy || !pct || !Number(data?.sharesRaw)} onClick={() => setConfirming(true)}
            className="sm:ml-auto bg-[#D4AF37] text-black hover:bg-[#F3E5AB] font-semibold">
            {busy ? <Loader2 size={16} className="animate-spin" /> : "Withdraw from pool"}
          </Button>
        </div>
        <p className="text-xs text-zinc-500">
          Funds go to the FSC Treasury, where "Send from treasury" can move them. The pool keeps working for other providers.{" "}
          <Link to="/liquidity" className="text-[#D4AF37] hover:underline">Open Pool page</Link>
        </p>
      </CardContent>
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent className="bg-[#0A0A0A] border-[#2A2411]">
          <AlertDialogHeader>
            <AlertDialogTitle>Withdraw {pct}% of the treasury's pool deposit?</AlertDialogTitle>
            <AlertDialogDescription>
              About {num((data?.fmi || 0) * pct / 100)} FMI and {num((data?.usdt || 0) * pct / 100, 2)} USDT will leave the pool and go to the treasury. The pool gets shallower, so swaps through it move the price more.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-lp-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-lp-confirm" onClick={withdraw} className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB]">Withdraw</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
